import {
  AgCartesianChartOptions,
  AgScatterSeriesOptions
} from 'ag-charts-community';
import dayjs from 'dayjs';
import { ScatterDataPoint } from './deal-details-graph.types';

export const providerColors: string[] = [
  '#5ac0fa',
  '#af87ba',
  '#ffff80',
  '#517455',
  '#ee4e64'
];

export function groupDataByProvider(
  data: ScatterDataPoint[]
): Record<string, ScatterDataPoint[]> {
  return data.reduce(
    (acc, point) => {
      if (!acc[point.provider]) {
        acc[point.provider] = [];
      }
      acc[point.provider].push(point);
      return acc;
    },
    {} as Record<string, ScatterDataPoint[]>
  );
}

export function getColorForProvider(
  provider: string,
  assignedProviderColors: Record<string, string>,
  index: number
): string {
  if (!assignedProviderColors[provider]) {
    const nextColor = providerColors[index];
    assignedProviderColors[provider] = nextColor;
  }
  return assignedProviderColors[provider];
}

export function pushToScatterGraph(
  series: AgScatterSeriesOptions[],
  color: string,
  size: number,
  provider: string,
  groupedData: Record<string, ScatterDataPoint[]>
): void {
  const bidData = groupedData[provider].filter(point => point.type === 'Bid');
  const offerData = groupedData[provider].filter(
    point => point.type === 'Offer'
  );

  series.push({
    type: 'scatter',
    fill: color,
    stroke: color,
    size: size,
    xKey: 'timestamp',
    yKey: 'premium',
    yName: `${provider}`,
    data: bidData,
    showInLegend: true,
    tooltip: {
      position: {
        type: 'pointer'
      },
      renderer: params => {
        const date = dayjs
          .utc(params.datum.timestamp)
          .format('DD MMM YYYY HH:mm:ss');
        const premium = params.datum.premium.toFixed(2);
        return {
          content: `<b>Time:</b> ${date}<br/><b>Provider:</b> ${provider}<br/><b>Type:</b> Bid<br/><b>Premium:</b> ${premium}`,
          backgroundColor: '#333333',
          color: 'white',
          title: null
        };
      }
    }
  });

  series.push({
    type: 'scatter',
    fill: 'transparent',
    stroke: color,
    strokeWidth: 2,
    size: size,
    xKey: 'timestamp',
    yKey: 'premium',
    yName: `${provider}`,
    data: offerData,
    showInLegend: true,
    tooltip: {
      position: {
        type: 'pointer'
      },
      renderer: params => {
        const date = dayjs
          .utc(params.datum.timestamp)
          .format('DD MMM YYYY HH:mm:ss');
        const premium = params.datum.premium.toFixed(2);
        return {
          content: `<b>Time:</b> ${date}<br/><b>Provider:</b> ${provider}<br/><b>Type:</b> Offer<br/><b>Premium:</b> ${premium}`,
          backgroundColor: '#333333',
          color: 'white',
          title: null
        };
      }
    }
  });
}

export function chartAxis(
  chartContainer: HTMLElement,
  series: AgScatterSeriesOptions[]
): AgCartesianChartOptions {
  return {
    container: chartContainer,
    background: {
      fill: '#1b2021'
    },
    series,
    padding: {
      right: 60,
      top: 70
    },
    axes: [
      {
        position: 'bottom',
        type: 'time',
        nice: false,
        gridLine: {
          style: [
            {
              stroke: '#5e615f',
              lineDash: [5, 5]
            }
          ]
        },
        line: {
          enabled: true
        },
        title: { text: 'Time (UTC)', color: '#ffffff', fontSize: 14 },
        label: {
          color: '#ffffff',
          fontSize: 12,
          formatter: params => {
            return dayjs(params.value).utc().format('HH:mm:ss');
          },
          rotation: 45
        }
      },
      {
        position: 'left',
        type: 'number',
        nice: false,
        gridLine: {
          style: [
            {
              stroke: '#5e615f',
              lineDash: [5, 5]
            }
          ]
        },
        line: {
          enabled: true
        },
        title: { text: 'Premium (USD)', color: '#ffffff', fontSize: 14 },
        label: {
          color: '#ffffff',
          fontSize: 12,
          formatter: params => {
            return params.value.toLocaleString();
          }
        }
      },
      {
        position: 'top',
        type: 'number',
        nice: false,
        line: {
          enabled: true
        },
        title: { text: '', color: '#ffffff', fontSize: 14 },
        label: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      {
        position: 'right',
        type: 'number',
        nice: false,
        line: {
          enabled: true
        },
        title: { text: '', color: '#ffffff', fontSize: 14 },
        label: {
          color: '#ffffff',
          fontSize: 12
        }
      }
    ],
    legend: {
      item: {
        paddingX: 15,
        label: {
          color: 'white',
          fontSize: 14
        }
      }
    }
  };
}
